/**
 * Schema for the event/action node graph — barrel export.
 *
 * Declares node types, their ports and properties, and the palette shown in
 * the left sidebar. Graph-state rules (max wires, duplicates, one event node
 * per object event) live in the logic model; everything here is static.
 */
import { getAvailableEvents, resolveObjectGestures } from '../wireFormat.js'
import { eventNode } from './eventNode.js'
import { GPIO_ENTITY_EVENTS, getGpioEventChoices, gpioBindingNode, gpioNode } from './gpioNode.js'
import { statePropertyOptions, stateTargetOptions } from '../../../../gpio/gpioEditorModel.js'
import {
    ACTION_NODE_TYPES,
    getActionCategories,
    getActionCategory,
    getActionCategoryId,
    getActionPaletteGroups,
    getActionVariantId,
} from './actionNodes.js'
import { CONDITION_OPERATORS, conditionNode, switchNode } from './flowNodes.js'
import { OBJECT_BINDINGS_PORT, OBJECT_EVENTS_PORT, PORT_KIND, isPortCompatible } from './ports.js'
import { filterPaletteForWire, resolveNodePorts, SWITCH_ELSE_PORT, switchCasePort } from '../graphPorts.js'

export {
    PORT_KIND, OBJECT_EVENTS_PORT, OBJECT_BINDINGS_PORT, isPortCompatible, ACTION_NODE_TYPES,
    GPIO_ENTITY_EVENTS, getGpioEventChoices,
    getActionCategories, getActionCategory, getActionCategoryId, getActionVariantId,
    CONDITION_OPERATORS, SWITCH_ELSE_PORT, switchCasePort,
}

export const LOGIC_NODE_SCHEMAS = Object.freeze({
    event: eventNode,
    gpio: gpioNode,
    gpioBinding: gpioBindingNode,
    condition: conditionNode,
    switch: switchNode,
    ...ACTION_NODE_TYPES,
})

export function getLogicNodeSchema(type) {
    return LOGIC_NODE_SCHEMAS[type] ?? null
}

/** A port of a node, including ports that follow its data (a switch's cases). */
export function getLogicNodePort(node, dir, portId) {
    const ports = resolveNodePorts(getLogicNodeSchema, node)
    return (dir === 'in' ? ports.inputs : ports.outputs).find(port => port.id === portId) ?? null
}

export function getInputPort(type, portId) {
    return getLogicNodeSchema(type)?.ports.inputs.find(port => port.id === portId) ?? null
}

export function getOutputPort(type, portId) {
    return getLogicNodeSchema(type)?.ports.outputs.find(port => port.id === portId) ?? null
}

/**
 * Event choices for an event node whose `source` is `sourceObject` (a node,
 * schema or obj type): its published events, then its gestures.
 */
export function getEventChoices(sourceObject) {
    return [...getAvailableEvents(sourceObject), ...Object.keys(resolveObjectGestures(sourceObject))]
}

/** The `events` output port of a UI object, or null when it emits no events. */
export function getObjectEventsPort(sourceObject) {
    return getEventChoices(sourceObject).length > 0 ? OBJECT_EVENTS_PORT : null
}

/** The `in` port of a UI object a GPIO state binding can drive, or null. */
export function getObjectBindingsPort(object) {
    const type = typeof object === 'string' ? object : object?.obj
    if (!type || type === 'page' || type === 'template_page') return null
    const config = typeof object === 'object' ? object : {}
    return statePropertyOptions(type).some(property => stateTargetOptions(type, config, property).length > 0)
        ? OBJECT_BINDINGS_PORT : null
}

/** Palette groups for the "Events & Actions" sidebar section, in display order. */
export function getLogicPalette() {
    return [
        {
            id: 'sources',
            label: 'Sources',
            color: gpioNode.color,
            entries: [{
                id: 'gpio', nodeType: 'gpio', label: gpioNode.label, icon: gpioNode.icon, color: gpioNode.color,
                preset: {}, title: 'GPIO entity of the selected plate: wire its events to event nodes',
            }],
        },
        {
            id: 'bindings',
            label: 'Bindings',
            color: gpioBindingNode.color,
            entries: [{
                id: 'gpioBinding', nodeType: 'gpioBinding', label: gpioBindingNode.label, icon: gpioBindingNode.icon,
                color: gpioBindingNode.color, preset: {},
                title: 'GPIO binding: keep an object property in sync with a GPIO entity (State Binding)',
            }],
        },
        {
            id: 'events',
            label: 'Events',
            color: eventNode.color,
            // The event itself is chosen in the node's properties, from its source.
            entries: [{
                id: 'event', nodeType: 'event', label: eventNode.label, icon: eventNode.icon, color: eventNode.color,
                preset: {}, title: 'Event: choose which event of its source in the properties',
            }],
        },
        {
            id: 'flow',
            label: 'Flow',
            color: conditionNode.color,
            entries: [conditionNode, switchNode].map(schema => ({
                id: schema.kind, nodeType: schema.kind, label: schema.label, icon: schema.icon, color: schema.color, preset: {},
                title: schema.kind === 'condition'
                    ? 'Condition: run the nodes after it only when its conditions hold'
                    : 'Switch: run the nodes of the first matching case',
            })),
        },
        ...getActionPaletteGroups(),
    ]
}

/**
 * Palette groups narrowed to nodes that could take an open wire end: for a
 * wire drawn from an output (`dir: 'out'`) a node whose input accepts `port`;
 * for a wire drawn from an input (`dir: 'in'`) a node whose output `port`
 * accepts.
 */
export function getPaletteForWire(dir, port) {
    return filterPaletteForWire(getLogicPalette(), getLogicNodeSchema, dir, port)
}
